import { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChange, getUserData } from '../services/auth';

const AuthContext = createContext({
  user: null,
  loading: true,
  refreshUser: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentFirebaseUser, setCurrentFirebaseUser] = useState(null);

  // Function to refresh user data from Firestore
  const refreshUser = async () => {
    if (currentFirebaseUser) {
      try {
        const userData = await getUserData(currentFirebaseUser.uid);
        setUser(userData);
        return userData;
      } catch (error) {
        console.error('[AUTH] Error refreshing user data:', error);
        return null;
      }
    }
    return null;
  };

  useEffect(() => {
    let mounted = true;
    let unsubscribe = null;
    
    // Always resolve after 3 seconds max to prevent infinite loading
    const timeoutId = setTimeout(() => {
      if (mounted) {
        setLoading(false);
      }
    }, 3000);

    // Initialize auth - components handle Google redirect themselves
    const initializeAuth = async () => {
      try {
        // Set up the auth state listener
        unsubscribe = onAuthStateChange(async (firebaseUser) => {
          if (!mounted) return;
          
          try {
            if (firebaseUser) {
              setCurrentFirebaseUser(firebaseUser);
              const userData = await getUserData(firebaseUser.uid);
              if (mounted) {
                setUser(userData);
              }
            } else {
              if (mounted) {
                setCurrentFirebaseUser(null);
                setUser(null);
              }
            }
          } catch (error) {
            console.error('[AUTH] Error loading user data:', error);
            if (mounted) {
              setUser(null);
            }
          } finally {
            if (mounted) {
              clearTimeout(timeoutId);
              setLoading(false);
            }
          }
        });
      } catch (error) {
        console.error('[AUTH] Error initializing auth:', error);
        if (mounted) {
          setLoading(false);
          clearTimeout(timeoutId);
        }
      }
    };

    initializeAuth();
    
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
